import moment from 'moment';
import { createSelector, OutputSelector } from 'reselect';

import { getLogCriteriaContainers } from '../declarations/log-filters/log-filters';
import { getLogColumns } from '../declarations/log/log';
import { ColumnsSettingsViewDict, DateRangePreset } from '../typings/common';
import { CriteriaContainer, FilterPartialExpression } from '../typings/filter';
import { PeriodFilterValue } from '../typings/filters';
import { LogSourceParams } from '../typings/log';
import { State } from '../typings/state';

export const selectLogSourceParams = createSelector(
    (state: State) => state.log.sourceParams,
    (res: LogSourceParams): LogSourceParams => res
);

interface MinMaxDate {
    minDate?: moment.Moment;
    maxDate?: moment.Moment;
}

/* Возвращает минимальную и максимальную даты выбора периода логгера */
export const selectLogPeriodLimits = (
    refDate: moment.Moment
): OutputSelector<State, MinMaxDate, (res: LogSourceParams) => MinMaxDate> => {
    return createSelector(
        selectLogSourceParams,
        (sourceParams: LogSourceParams): MinMaxDate => {
            if (!sourceParams) {
                return {};
            }

            return {
                minDate: refDate
                    .clone()
                    // значение в 1 год взято с потолка
                    // обсудить с бэкэндом настоящую цифру
                    .subtract(1, 'years')
                    .date(1),
                maxDate: refDate.clone()
            };
        }
    );
};

export const selectLogFilterContainers = createSelector(
    selectLogSourceParams,
    (params?: LogSourceParams): CriteriaContainer[] => {
        if (!params) {
            return [];
        }
        return getLogCriteriaContainers();
    }
);

export const selectLogFilterExpressions = createSelector(
    selectLogSourceParams,
    (sourceParams: LogSourceParams): FilterPartialExpression[] => {
        if (!sourceParams || !sourceParams.filter) {
            return [];
        }

        return sourceParams.filter;
    }
);

export const selectLogPeriodFilterValue = createSelector(
    selectLogSourceParams,
    (sourceParams: LogSourceParams): PeriodFilterValue => {
        if (!sourceParams) {
            return {
                dateRangePreset: DateRangePreset.allTime
            };
        }

        const {
            period: { dateFrom, dateTo, preset }
        } = sourceParams;

        if (dateFrom && dateTo) {
            return {
                dateFrom: moment(dateFrom),
                dateTo: moment(dateTo)
            };
        } else {
            return {
                dateRangePreset: preset || DateRangePreset.allTime
            };
        }
    }
);

export const selectLogsColumns = createSelector(
    (state: State) => state.log.login,
    (state: State) => state.log.rowExpanded,
    (state: State) => state.log.rowsCanExpand,
    (state: State) => state.log.columnsSettings,
    (state: State) => state.log.maxHeightCleared,
    (state: State) => state.commonData.features.isBidCorrectionSearchEnabled,
    (
        login: string,
        rowExpanded: Map<number, boolean>,
        rowsCanExpand: Map<number, boolean>,
        columnsSettings: ColumnsSettingsViewDict,
        maxHeightCleared: boolean,
        isBidCorrectionSearchEnabled?: boolean
    ) => {
        return getLogColumns(
            rowExpanded,
            rowsCanExpand,
            columnsSettings,
            login,
            maxHeightCleared,
            isBidCorrectionSearchEnabled
        );
    }
);
